import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  from,
  HttpLink,
  InMemoryCache,
  Observable,
  split,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { useI18n } from '@i18n';
import { context, SpanKind, SpanStatusCode, trace } from '@opentelemetry/api';
import { Kind, OperationDefinitionNode } from 'graphql';
import { createClient } from 'graphql-ws';
import { useSnackbar } from 'notistack';
import { PropsWithChildren, useEffect, useMemo } from 'react';
import { useAccessToken } from '@/components/session/AccessTokenContext';
import { captureClientException } from '@/utils/posthogErrorTracking';
import NetworkRetrySnackbarAction from '../components/snackbars/NetworkRetrySnackbarAction';
import { isSharedPage } from '../guards/AuthenticationGuard';
import { isTracingEnabled } from '../tracing';

type HasuraApiProviderProps = PropsWithChildren<{
  clientCacheAndNetworkPolicy: boolean;
}>;

// For reference see: https://www.apollographql.com/docs/react/data/subscriptions
export default function HasuraApiProvider({ children, clientCacheAndNetworkPolicy }: HasuraApiProviderProps) {
  const { t, language } = useI18n();
  const { enqueueSnackbar } = useSnackbar();
  const { accessToken } = useAccessToken();

  useEffect(() => {
    if (!accessToken && import.meta.env.VITE_DEVELOP_OFFLINE !== 'true' && !isSharedPage()) {
      enqueueSnackbar(t('HasuraApiProvider_AuthToken-Error'), {
        variant: 'error',
      });
    }
  }, [accessToken, enqueueSnackbar, t]);

  /**
   * If a mutation fails due to a network error, we give the user the chance to retry it once the network is back.
   */
  const retryLink = useMemo(
    () =>
      new RetryLink({
        attempts: {
          retryIf(error, operation) {
            const isMutation = operation.query.definitions.some(
              (def) => def.kind === 'OperationDefinition' && def.operation === 'mutation',
            );

            if (!error.message.includes('NetworkError') || !isMutation) {
              return false;
            }

            const retryPromise = new Promise<boolean>((resolve) => {
              enqueueSnackbar(t('HasuraApiProvider_RetryLinkSnackbar-Error'), {
                variant: 'warning',
                autoHideDuration: 45000,
                action: (snackbarKey) => <NetworkRetrySnackbarAction resolve={resolve} snackbarKey={snackbarKey} />,
                onExited: () => resolve(false),
              });
            });

            return retryPromise;
          },
        },
      }),
    [enqueueSnackbar, t],
  );

  const errorLink = useMemo(
    () => [
      onError(({ graphQLErrors, networkError, operation }) => {
        if (graphQLErrors) {
          graphQLErrors.forEach((graphqlError) => {
            // ACTION_PLAN_NOT_FOUND: transient state — race condition, deleted plan, or inactive
            // scenario version. ACTION_NOT_FOUND: stale action_id after reapplyActions regenerated
            // the action rows. Both are logged server-side; neither is actionable in PostHog.
            if (
              graphqlError.extensions?.code === 'ACTION_PLAN_NOT_FOUND' ||
              graphqlError.extensions?.code === 'ACTION_NOT_FOUND'
            ) {
              return;
            }
            captureClientException(graphqlError, {
              source: 'apollo_graphql_error',
              operationName: operation.operationName,
              path: graphqlError.path,
              code: graphqlError.extensions?.code,
            });
          });
        }

        if (networkError) {
          captureClientException(networkError, {
            source: 'apollo_network_error',
            operationName: operation.operationName,
          });
        }
      }),
    ],
    [],
  );

  const openTelemetryLink = useMemo(
    () =>
      isTracingEnabled()
        ? [
            new ApolloLink((operation, forward) => {
              // We need an obsverable here to be able to correlate the GraphQL span
              //with the HTTP request span. Otherwise, you get two unrelated spans in
              // the trace.
              return new Observable((observer) => {
                const tracer = trace.getTracer('apollo-client');
                const type = operation.query.definitions.find((d) => d.kind === Kind.OPERATION_DEFINITION)?.operation;

                const span = tracer.startSpan(`GraphQL ${operation.operationName || 'operation'}`, {
                  kind: SpanKind.CLIENT,
                  attributes: {
                    'graphql.operation.name': operation.operationName || 'unknown',
                    'graphql.variables': JSON.stringify(operation.variables),
                    'graphql.type': type,
                  },
                });

                const contextWithSpan = trace.setSpan(context.active(), span);
                context.with(contextWithSpan, () => {
                  forward(operation).subscribe({
                    next: (result) => {
                      if (result.errors?.length) {
                        span.recordException(new Error(result.errors.map((e) => e.message).join(', ')));
                        span.setStatus({
                          code: SpanStatusCode.ERROR,
                          message: 'GraphQL errors',
                        });
                      } else {
                        span.setStatus({ code: SpanStatusCode.OK });
                      }
                      observer.next(result);
                    },
                    error: (error) => {
                      span.recordException(error);
                      span.setStatus({
                        code: SpanStatusCode.ERROR,
                        message: error.message,
                      });
                      span.end();
                      observer.error(error);
                    },
                    complete: () => {
                      span.end();
                      observer.complete();
                    },
                  });
                });

                return () => {
                  span.end();
                };
              });
            }),
          ]
        : [],
    [],
  );

  const client = useMemo(() => {
    // Local offline dev (VITE_DEVELOP_OFFLINE=true): there is no Auth0 token, so authenticate
    // to Hasura with the admin secret and impersonate a dev user id. The backend getSessionData
    // remote resolver requires a user id (userId/auth0Id/email), so the admin secret alone is not
    // enough. We impersonate user id 1 (the org admin in the seeded org 1) so admin-gated pages
    // (e.g. Org settings / integrations) are reachable in offline dev. Never active in production,
    // where VITE_DEVELOP_OFFLINE is unset and the Bearer token is used.
    const authHeaders: Record<string, string> =
      import.meta.env.VITE_DEVELOP_OFFLINE === 'true'
        ? {
            'x-hasura-admin-secret': import.meta.env.VITE_HASURA_ADMIN_SECRET ?? '',
            'x-hasura-user-id': '1',
            'x-hasura-org-id': '1',
          }
        : {
            Authorization: `Bearer ${accessToken}`,
          };

    const i18nHeaders = {
      'x-lang': language,
    };

    const httpLink = new HttpLink({
      uri: import.meta.env.VITE_HASURA_ENDPOINT,

      headers: {
        ...authHeaders,
        ...i18nHeaders,
      },
      credentials: 'include',
    });

    const getIsWebsocketDisabled = () => {
      try {
        return localStorage.getItem('disableWebsocket') === 'true';
      } catch {
        return false;
      }
    };

    // it is a dummy search param that breaks the websocket connection
    const searchParam = getIsWebsocketDisabled() ? '?dummyQueryString' : '';

    const wsLink = new GraphQLWsLink(
      createClient({
        url:
          import.meta.env.VITE_SANDBOX === 'true'
            ? `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/api/ws${searchParam}`
            : import.meta.env.VITE_HASURA_ENDPOINT_WS + searchParam,
        connectionParams: () => ({
          headers: {
            ...authHeaders,
          },
        }),
      }),
    );

    const link = split(
      ({ query }) => {
        const { kind, operation } = getMainDefinition(query) as OperationDefinitionNode;
        return kind === 'OperationDefinition' && operation === 'subscription';
      },
      wsLink,
      httpLink,
    );

    return new ApolloClient({
      link: from([...openTelemetryLink, ...errorLink, retryLink, link]),
      cache: new InMemoryCache(),
      defaultOptions: clientCacheAndNetworkPolicy
        ? {
            watchQuery: {
              fetchPolicy: 'cache-and-network',
            },
            query: {
              fetchPolicy: 'network-only',
            },
          }
        : undefined,
    });
  }, [accessToken, retryLink, errorLink, language, openTelemetryLink, clientCacheAndNetworkPolicy]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
